import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Sparkles, MapPin, Languages, ShieldCheck, ChevronRight, Check, Loader2,
  FileText, Stethoscope, Search, RotateCcw,
} from 'lucide-react';
import { api } from '../../lib/api';
import { useOrg } from '../../hooks/useOrg';
import { SPECIALTIES } from '../../lib/referrals/packet';
import { isFirmLike } from '../../lib/orgTypes';

// Patient self-service provider search — the same engine the attorney's
// concierge uses, pointed at the patient's own case file. The file is the
// guardrail: the injury on record picks the specialty, the address on file
// anchors the search, the language preference carries into matching, and
// the request lands in the law firm's referral list (via: 'patient') so
// the legal team always sees what their client set up.

const PROVIDER_TYPE_LABELS = {
  chiropractic: 'Chiropractic', mri_imaging: 'Diagnostic imaging', ortho: 'Orthopedics',
  surgery_center: 'Surgery center', hospital: 'Hospital', plastic_surgery: 'Plastic surgery',
  other: 'Medical practice',
};

export default function FindCarePage() {
  const { memberships } = useOrg();
  // Requests belong to the firm; the API resolves that server-side, but the
  // org header just needs any org the patient belongs to.
  const orgId = (memberships.find((m) => isFirmLike(m.organization?.type)) || memberships[0])?.org_id;

  const [file, setFile] = useState(null);
  const [results, setResults] = useState(null);
  const [provider, setProvider] = useState(null);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(null);
  const [error, setError] = useState('');
  // The patient can search beyond what the file suggests — a different
  // area (staying with family, travelling), another specialty, more results.
  const [near, setNear] = useState('');
  const [specialty, setSpecialty] = useState('');
  const [spanish, setSpanish] = useState(false);
  const [searching, setSearching] = useState(false);
  const [searched, setSearched] = useState(null); // the criteria the current results came from
  const [officeId, setOfficeId] = useState(''); // which of the practice's offices, when it has several

  const runSearch = useCallback(async (criteria) => {
    setSearching(true); setError('');
    try {
      const res = await api.searchReferralProviders(orgId, {
        ...(criteria.specialty ? { specialty: criteria.specialty } : {}),
        ...(criteria.near ? { near: criteria.near } : {}),
        ...(criteria.language ? { language: criteria.language } : {}),
        limit: 10,
      });
      setResults(res.results || []);
      setSearched(criteria);
    } catch (err) {
      setError(err.message || 'Could not search providers');
    } finally {
      setSearching(false);
    }
  }, [orgId]);

  const fileCriteria = (f) => ({ specialty: f.specialty || '', near: f.near || '', language: f.language || null });

  const load = useCallback(async () => {
    if (!orgId) return;
    try {
      const f = await api.getMyCareFile(orgId);
      setFile(f);
      setNear(f.near || ''); setSpecialty(f.specialty || ''); setSpanish(f.language === 'es');
      await runSearch(fileCriteria(f));
    } catch (err) {
      setError(err.status === 404
        ? 'Your case file is not set up for self-scheduling yet — ask your legal team, or browse the provider directory instead.'
        : (err.message || 'Could not load your care options'));
    }
  }, [orgId, runSearch]);

  useEffect(() => { load(); }, [load]);

  const isOwnSearch = !!(file && searched) && (
    searched.near !== (file.near || '') || searched.specialty !== (file.specialty || '') || (searched.language === 'es') !== (file.language === 'es'));

  const resetToFile = () => {
    setNear(file.near || ''); setSpecialty(file.specialty || ''); setSpanish(file.language === 'es');
    runSearch(fileCriteria(file));
  };

  const send = async () => {
    if (!provider) return;
    setSending(true); setError('');
    try {
      const chosen = searched?.specialty || file?.specialty || null;
      const spec = chosen ? SPECIALTIES[chosen] : null;
      const r = await api.createMyCareRequest(orgId, {
        provider_org_id: provider.org_id,
        location_id: officeId || provider.nearest_location?.id || undefined,
        specialty: chosen,
        service: spec ? spec.service : null,
      });
      setSent(r);
    } catch (err) {
      setError(err.message || 'Could not send your request');
    } finally {
      setSending(false);
    }
  };

  const specialtyLabel = file?.specialty ? SPECIALTIES[file.specialty].label : null;

  if (sent) {
    return (
      <div className="max-w-[640px] mx-auto px-5 py-10 text-center">
        <div className="w-[52px] h-[52px] rounded-full bg-emerald-500/15 text-emerald-400 flex items-center justify-center mx-auto mb-4">
          <Check size={22} />
        </div>
        <h1 className="text-[19px] font-bold text-primary-c">Request sent</h1>
        <p className="text-[13.5px] text-muted-c mt-2 max-w-[460px] mx-auto leading-relaxed">
          {provider?.name} and your legal team have both been notified. The provider has one
          hour to accept; once they do, they will schedule your first visit and you will hear
          back with the date and time. Your file — as prepared by your attorney — went along
          with the request, so there is nothing to fill out or bring.
        </p>
        <Link
          to="/client"
          className="inline-block mt-6 px-5 py-[10px] rounded-[10px] text-[13px] font-semibold text-white gradient-accent shadow-glow"
        >
          Back to my appointments
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-[760px] mx-auto px-5 py-7">
      <div className="flex items-center gap-2 mb-1">
        <Sparkles size={15} className="text-accent-light" />
        <span className="text-[11px] uppercase tracking-wide text-accent-light font-semibold">Find care for my case</span>
      </div>
      <h1 className="text-[22px] font-bold text-primary-c mb-1">Choose a provider</h1>
      <p className="text-[13px] text-muted-c max-w-[560px] mb-6">
        These options are matched to your case — the injury on file, your location, and your
        language — and your legal team sees every request you make.
      </p>

      {error && (
        <div className="text-[12.5px] text-red-400 bg-red-500/10 border border-red-500/20 rounded-[10px] px-3.5 py-2.5 mb-5">
          {error} <Link to="/client/directory" className="underline font-semibold">Open the directory</Link>
        </div>
      )}

      {!file && !error && (
        <p className="text-[13px] text-muted-c flex items-center gap-2 py-8 justify-center">
          <Loader2 size={15} className="animate-spin" /> Reading your case file…
        </p>
      )}

      {file && (
        <div className="rounded-[14px] border border-theme bg-elevated p-4 mb-5">
          <div className="text-[11px] uppercase tracking-wide text-muted-c font-semibold mb-2.5">Your case, as your legal team set it up</div>
          <div className="flex flex-wrap gap-x-5 gap-y-2 text-[12.5px]">
            {file.diagnosis && (
              <span className="flex items-center gap-1.5 text-primary-c"><Stethoscope size={13} className="text-accent-light" /> {file.diagnosis}</span>
            )}
            {specialtyLabel && (
              <span className="flex items-center gap-1.5 text-primary-c"><Sparkles size={13} className="text-accent-light" /> Recommended: {specialtyLabel}</span>
            )}
            {file.near && (
              <span className="flex items-center gap-1.5 text-primary-c"><MapPin size={13} className="text-accent-light" /> Near {file.near}</span>
            )}
            {file.language === 'es' && (
              <span className="flex items-center gap-1.5 text-primary-c"><Languages size={13} className="text-accent-light" /> Spanish-speaking care</span>
            )}
            <span className="flex items-center gap-1.5 text-muted-c"><FileText size={13} /> {file.packet_count} record{file.packet_count === 1 ? '' : 's'} travel with your request</span>
          </div>
        </div>
      )}

      {/* Search beyond the file's defaults */}
      {file && !provider && (
        <form
          onSubmit={(e) => { e.preventDefault(); runSearch({ specialty, near: near.trim(), language: spanish ? 'es' : null }); }}
          className="rounded-[14px] border border-theme bg-elevated p-3.5 mb-5"
        >
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="flex items-center gap-2 flex-1 bg-tertiary border border-theme rounded-[10px] px-3 h-[40px]">
              <MapPin size={13} className="text-muted-c shrink-0" />
              <input value={near} onChange={(e) => setNear(e.target.value)} placeholder="City, neighborhood or ZIP" className="flex-1 bg-transparent text-[13px] text-primary-c placeholder:text-muted-c focus:outline-none min-w-0" />
            </div>
            <select value={specialty} onChange={(e) => setSpecialty(e.target.value)} className="h-[40px] px-3 rounded-[10px] bg-tertiary border border-theme text-[13px] text-primary-c focus:outline-none">
              <option value="">Any specialty</option>
              {Object.entries(SPECIALTIES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
            <label className="inline-flex items-center gap-2 h-[40px] px-3 rounded-[10px] bg-tertiary border border-theme text-[12.5px] text-primary-c cursor-pointer whitespace-nowrap">
              <input type="checkbox" checked={spanish} onChange={(e) => setSpanish(e.target.checked)} /> <Languages size={13} className="text-accent-light" /> Spanish-speaking
            </label>
            <button type="submit" disabled={searching} className="h-[40px] px-4 rounded-[10px] text-[13px] font-semibold text-white gradient-accent shadow-glow disabled:opacity-50 inline-flex items-center justify-center gap-1.5 whitespace-nowrap">
              {searching ? <Loader2 size={13} className="animate-spin" /> : <Search size={13} />} Search
            </button>
          </div>
          <div className="flex items-center justify-between gap-3 flex-wrap mt-2">
            <p className="text-[11px] text-muted-c">
              {isOwnSearch
                ? 'Showing your own search. Your records still travel with any request you make.'
                : 'Matched to your case. Change the area or specialty if you need care somewhere else.'}
            </p>
            {isOwnSearch && (
              <button type="button" onClick={resetToFile} className="text-[11px] font-semibold text-accent-light inline-flex items-center gap-1">
                <RotateCcw size={11} /> Back to my case's match
              </button>
            )}
          </div>
        </form>
      )}

      {/* Providers */}
      {results && !provider && (
        <div className="flex flex-col gap-3">
          {searching && <p className="text-[12px] text-muted-c flex items-center gap-2"><Loader2 size={13} className="animate-spin" /> Searching…</p>}
          {results.length === 0 && !searching && (
            <p className="text-[13px] text-muted-c">
              No matching providers{searched?.near ? ` near ${searched.near}` : ''} — try a wider area or another specialty, or <Link to="/client/directory" className="text-accent-light underline">browse the full directory</Link>.
            </p>
          )}
          {results.map((p) => (
            <button
              key={p.org_id}
              onClick={() => { setProvider(p); setOfficeId(p.nearest_location?.id || p.locations?.[0]?.id || ''); }}
              className="w-full text-left rounded-[14px] border border-theme bg-elevated p-4 hover:border-strong hover:bg-hover-surface transition-all flex items-center gap-4 group"
            >
              <div className="w-[42px] h-[42px] rounded-[12px] gradient-accent flex items-center justify-center flex-shrink-0 text-white font-bold text-[15px]">
                {p.name.charAt(0)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-0.5">
                  <span className="text-[14px] font-semibold text-primary-c">{p.name}</span>
                  {p.accepts_lop === true && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-[6px] text-[10.5px] font-semibold bg-success-soft text-success">
                      <ShieldCheck size={10} /> Works with your case type
                    </span>
                  )}
                </div>
                <div className="text-[12px] text-muted-c">
                  {(p.specialties?.length ? p.specialties.join(' · ') : PROVIDER_TYPE_LABELS[p.provider_type]) || 'Medical practice'}
                  {' · '}{p.nearest_location && (p.locations || []).length > 1 ? `${p.nearest_location.name} · ` : ''}{[p.city, p.state].filter(Boolean).join(', ')}
                  {p.distance_miles != null ? ` · ${p.distance_miles.toFixed(1)} mi` : ''}
                  {(p.locations || []).length > 1 ? ` · ${p.locations.length} offices` : ''}
                </div>
              </div>
              <ChevronRight size={15} className="text-muted-c group-hover:text-accent-light flex-shrink-0" />
            </button>
          ))}
        </div>
      )}

      {/* Time */}
      {provider && (
        <div>
          <div className="rounded-[14px] border border-theme bg-elevated p-4 mb-4 flex items-center gap-3">
            <div className="w-[38px] h-[38px] rounded-[10px] gradient-accent flex items-center justify-center text-white font-bold text-[14px] flex-shrink-0">
              {provider.name.charAt(0)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-semibold text-primary-c">{provider.name}</div>
              <div className="text-[11.5px] text-muted-c">{[provider.city, provider.state].filter(Boolean).join(', ')}</div>
            </div>
            <button onClick={() => setProvider(null)} className="text-[12px] font-semibold text-muted-c hover:text-accent-light">Change</button>
          </div>

          {(provider.locations || []).length > 1 && (
            <div className="rounded-[14px] border border-theme bg-elevated p-4 mb-4">
              <label className="block text-[12px] font-semibold text-secondary-c mb-2">Which office?</label>
              <div className="flex flex-col gap-1.5">
                {provider.locations.map((l) => (
                  <label key={l.id} className={`flex items-center gap-2.5 px-3 py-2 rounded-[10px] border cursor-pointer text-[12.5px] ${officeId === l.id ? 'border-accent-light bg-accent-glow text-primary-c' : 'border-theme bg-tertiary text-secondary-c'}`}>
                    <input type="radio" name="office" checked={officeId === l.id} onChange={() => setOfficeId(l.id)} />
                    <MapPin size={12} className="text-accent-light shrink-0" />
                    <span className="font-medium">{l.name}</span>
                    <span className="text-muted-c">{[l.city, l.state].filter(Boolean).join(', ')}{l.distance_miles != null ? ` · ${l.distance_miles.toFixed(1)} mi` : ''}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          <div className="rounded-[14px] border border-theme bg-elevated p-4 mb-5 text-[12.5px] text-muted-c">
            <span className="font-semibold text-primary-c">How this works:</span> {provider.name} gets your request with your file and has one hour to accept. Once they accept, you pick your first visit from their open times{(provider.locations || []).length > 1 ? ' at the office you chose' : ''}.
          </div>

          <div className="flex items-center justify-between gap-3 flex-wrap">
            <p className="text-[12px] text-muted-c max-w-[420px]">
              Your records go with the request exactly as your legal team prepared them — you
              don't fill out anything.
            </p>
            <button
              onClick={send}
              disabled={sending}
              className="px-5 py-[10px] rounded-[10px] text-[13px] font-semibold text-white gradient-accent shadow-glow disabled:opacity-50 flex items-center gap-1.5"
            >
              {sending ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />} Send my request
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
